import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, 
  User, 
  MapPin, 
  LogOut, 
  ChevronRight,
  Clock,
  CheckCircle,
  Truck,
  ShoppingBag,
  Lock,
  Unlock,
  Info,
  Heart,
  ShoppingCart,
  Trash2
} from 'lucide-react';
import { useWishlist } from '@/hooks/useWishlist';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { Button } from '@/components/ui/Button';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface Pedido {
  id: string;
  numeroPedido: string;
  status: string;
  faturamentoTotal: number;
  createdAt: string;
  items: Array<{
    id: string;
    quantidade: number;
    precoUnitario: number;
    produto: {
      nome: string;
      fotos: string[];
    };
  }>;
}

interface Cliente {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  endereco: string;
  cidade: string;
  estado: string;
  temAcessoSalaSecreta: boolean;
}

export function MinhaConta() {
  const navigate = useNavigate();
  const { logout, isAuthenticated, tipo } = useAuthStore();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState<'pedidos' | 'dados' | 'sala-secreta' | 'wishlist'>('pedidos');
  
  const { wishlist, toggleWishlist } = useWishlist();
  const adicionarAoCarrinho = useCarrinhoStore(state => state.adicionarItem);

  useEffect(() => {
    if (!isAuthenticated || tipo !== 'cliente') {
      navigate('/login', { state: { from: { pathname: '/minha-conta' } } });
      return;
    }
    carregarDados();
  }, [isAuthenticated, tipo]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [clienteRes, pedidosRes] = await Promise.all([
        api.get('/clientes/me'),
        api.get('/pedidos/meus'),
      ]);
      setCliente(clienteRes.data);
      setPedidos(pedidosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ENTREGUE':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'ENVIADO':
        return <Truck className="w-5 h-5 text-blue-500" />;
      case 'PRODUCAO':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <ShoppingBag className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'NOVO': 'Novo',
      'AGUARDANDO_PAGAMENTO': 'Aguardando Pagamento',
      'PAGO_CONFIRMADO': 'Confirmado',
      'PRODUCAO': 'Em Produção',
      'AGUARDANDO_SEPARACAO': 'Preparando Envio',
      'EM_SEPARACAO': 'Preparando Envio',
      'PRONTO_EXPEDICAO': 'Pronto para Envio',
      'ENVIADO': 'Enviado',
      'ENTREGUE': 'Entregue',
      'CANCELADO': 'Cancelado',
    };
    return labels[status] || status;
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatarPreco = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Minha Conta</h1>
            <p className="text-gray-500">Bem-vindo, {cliente?.nome || 'Cliente'}</p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => setAbaAtiva('pedidos')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  abaAtiva === 'pedidos'
                    ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-l-4 border-[#FF6B35]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Package className="w-5 h-5" />
                Meus Pedidos
              </button>
              <button
                onClick={() => setAbaAtiva('dados')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  abaAtiva === 'dados'
                    ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-l-4 border-[#FF6B35]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <User className="w-5 h-5" />
                Meus Dados
              </button>
              <button
                onClick={() => setAbaAtiva('sala-secreta')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  abaAtiva === 'sala-secreta'
                    ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-l-4 border-[#FF6B35]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {cliente?.temAcessoSalaSecreta ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                Sala Secreta
              </button>
              <button
                onClick={() => setAbaAtiva('wishlist')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  abaAtiva === 'wishlist'
                    ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-l-4 border-[#FF6B35]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Heart className="w-5 h-5" />
                Meus Favoritos
              </button>
            </div>

            {/* Resumo */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4">
              <h3 className="font-medium text-gray-900 mb-3">Resumo</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total de pedidos</span>
                  <span className="font-medium">{pedidos.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Em andamento</span>
                  <span className="font-medium">
                    {pedidos.filter(p => ['NOVO', 'AGUARDANDO_PAGAMENTO', 'PAGO_CONFIRMADO', 'PRODUCAO', 'AGUARDANDO_SEPARACAO', 'EM_SEPARACAO', 'PRONTO_EXPEDICAO', 'ENVIADO'].includes(p.status)).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Entregues</span>
                  <span className="font-medium text-green-600">
                    {pedidos.filter(p => p.status === 'ENTREGUE').length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="lg:col-span-3">
            {abaAtiva === 'pedidos' ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Meus Pedidos
                </h2>

                {pedidos.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Nenhum pedido ainda
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Você ainda não fez nenhuma compra
                    </p>
                    <Link to="/catalogo">
                      <Button variant="nuvem">Ver produtos</Button>
                    </Link>
                  </div>
                ) : (
                  pedidos.map((pedido) => (
                    <div
                      key={pedido.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              Pedido #{pedido.numeroPedido}
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              {formatarData(pedido.createdAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusIcon(pedido.status)}
                            <span className="text-sm text-gray-600">
                              {getStatusLabel(pedido.status)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {formatarPreco(Number(pedido.faturamentoTotal))}
                          </p>
                          <p className="text-sm text-gray-500">
                            {pedido.items.length} item(s)
                          </p>
                        </div>
                      </div>

                      {/* Itens do pedido */}
                      <div className="border-t pt-4">
                        <div className="space-y-3">
                          {pedido.items.slice(0, 2).map((item) => (
                            <div key={item.id} className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                {item.produto.fotos?.[0] ? (
                                  <img
                                    src={item.produto.fotos[0]}
                                    alt={item.produto.nome}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                ) : (
                                  <Package className="w-6 h-6 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                  {item.produto.nome}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Qtd: {item.quantidade}
                                </p>
                              </div>
                              <p className="text-sm font-medium text-gray-900">
                                {formatarPreco(Number(item.precoUnitario) * item.quantidade)}
                              </p>
                            </div>
                          ))}
                          {pedido.items.length > 2 && (
                            <p className="text-sm text-gray-500">
                              +{pedido.items.length - 2} item(s) mais
                            </p>
                          )}
                        </div>

                        <Link
                          to={`/pedido/${pedido.id}`}
                          className="inline-flex items-center gap-1 text-[#FF6B35] text-sm font-medium mt-4 hover:underline"
                        >
                          Ver detalhes
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                  Meus Dados
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome completo
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-900">{cliente?.nome}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {cliente?.email}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {cliente?.telefone || 'Não informado'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Endereço
                    </label>
                    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div className="text-gray-900">
                        {cliente?.endereco ? (
                          <>
                            <p>{cliente.endereco}</p>
                            <p className="text-gray-500">
                              {cliente.cidade}, {cliente.estado}
                            </p>
                          </>
                        ) : abaAtiva === 'sala-secreta' ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 text-white">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm">
                      {cliente?.temAcessoSalaSecreta ? (
                        <Unlock className="w-8 h-8 text-[#FF6B35]" />
                      ) : (
                        <Lock className="w-8 h-8 text-[#FF6B35]" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">Sala Secreta</h2>
                      <p className="text-gray-400 text-sm italic">Onde a exclusividade ganha vida</p>
                    </div>
                  </div>
                  
                  {cliente?.temAcessoSalaSecreta ? (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-bold uppercase tracking-wider border border-green-500/30">
                      Acesso Liberado
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs font-bold uppercase tracking-wider border border-yellow-500/30">
                      Acesso Bloqueado
                    </div>
                  )}
                </div>

                <div className="p-8 space-y-6">
                  {cliente?.temAcessoSalaSecreta ? (
                    <>
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Parabéns! Você faz parte do nosso círculo interno.</h3>
                        <p className="text-gray-600 leading-relaxed">
                          Agora você tem acesso a modelos 3D exclusivos, protótipos em fase de teste e peças com tiragem limitada que não estão disponíveis no catálogo público.
                        </p>
                        <div className="p-4 bg-orange-50 border border-orange-100 rounded-xl flex items-start gap-4">
                          <CheckCircle className="w-6 h-6 text-[#FF6B35] shrink-0" />
                          <p className="text-sm text-orange-900 font-medium">
                            Seu acesso foi liberado automaticamente após sua primeira compra confirmada. Aproveite as novidades!
                          </p>
                        </div>
                        <Link to="/sala-secreta">
                          <Button className="w-full sm:w-auto bg-[#FF6B35] hover:bg-[#E55A24] text-white font-bold h-12 px-8">
                            Entrar na Sala Secreta
                          </Button>
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Como desbloquear a Sala Secreta?</h3>
                        <p className="text-gray-600 leading-relaxed">
                          A Sala Secreta da Fort3D é um espaço reservado apenas para colecionadores e entusiastas que já experimentaram nossa qualidade.
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-4 mt-6">
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-center">
                            <ShoppingBag className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <h4 className="font-bold text-gray-900 mb-1">Passo 1</h4>
                            <p className="text-xs text-gray-500">Realize sua primeira compra em nosso site.</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-center opacity-50">
                            <Unlock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <h4 className="font-bold text-gray-900 mb-1">Passo 2</h4>
                            <p className="text-xs text-gray-500">Acesso liberado automaticamente!</p>
                          </div>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-xl flex gap-3 text-blue-900 italic mt-6 border border-blue-100">
                          <Info className="w-5 h-5 text-blue-600 shrink-0" />
                          <p className="text-sm">
                            Assim que seu primeiro pedido for confirmado pelo sistema, você receberá um token de acesso exclusivo para a Sala Secreta.
                          </p>
                        </div>

                        <div className="pt-4">
                          <Link to="/catalogo">
                            <Button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-bold">
                              Ver Catálogo para Primeira Compra
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            ) : abaAtiva === 'wishlist' ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Meus Favoritos
                </h2>

                {wishlist.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Sua lista está vazia</h3>
                    <p className="text-gray-500 mb-6">Salve os produtos que você mais gosta para encontrá-los facilmente depois.</p>
                    <Link to="/catalogo">
                      <Button variant="nuvem">Explorar Catálogo</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {wishlist.map((item: any) => (
                      <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                        <div className="relative aspect-video bg-gray-100">
                          {item.produto.fotos?.[0] ? (
                            <img src={item.produto.fotos[0]} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-full h-full p-8 text-gray-300" />
                          )}
                          <button 
                            onClick={() => toggleWishlist(item.produtoId)}
                            className="absolute top-2 right-2 p-2 bg-white/80 backdrop-blur rounded-full text-rose-500 shadow-sm transition-colors hover:bg-rose-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <h3 className="font-bold text-gray-900 line-clamp-1 mb-1">{item.produto.nome}</h3>
                          <p className="text-[#FF6B35] font-bold text-lg mb-4">{formatarPreco(Number(item.produto.precoVenda))}</p>
                          
                          <div className="mt-auto flex gap-2">
                             <Button 
                              variant="nuvem" 
                              className="flex-1 text-xs px-2"
                              onClick={() => adicionarAoCarrinho(item.produto, 1)}
                              disabled={item.produto.estoqueDisponivel === 0}
                             >
                                <ShoppingCart className="w-3 h-3 mr-1" /> Carrinho
                             </Button>
                             <Link to={`/produto/${item.produto.slug}`} className="flex-1">
                                <Button variant="outline" className="w-full text-xs px-2">Ver Detalhes</Button>
                             </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
                          'Não informado'
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button variant="outline">
                      Editar dados
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MinhaConta;
